import { useMemo } from "react";
import { LuMailCheck, LuMessageCircle, LuSearch, LuTimer, LuUsers, LuX } from "react-icons/lu";
import { useSearchParams } from "react-router-dom";

import { useOpenJobs } from "../api";
import CareersShell, { TopBar } from "../components/CareersShell";
import Select from "../components/Select";
import RoleList, { RoleListSkeleton } from "../components/RoleList";
import { daysUntil } from "../lib/dates";
import { careersShareUrl } from "../lib/share";
import useDocumentMeta from "../lib/useDocumentMeta";

const ALL = "all";
const DEFAULTS = { q: "", dept: ALL, sort: "newest" };
const SORTS = {
  newest: { label: "Newest", compare: (a, b) => new Date(b.created_at) - new Date(a.created_at) },
  closing: {
    label: "Closing soonest",
    compare: (a, b) => (daysUntil(a.expiry_date) ?? 9999) - (daysUntil(b.expiry_date) ?? 9999),
  },
};

const META = {
  title: "Careers at Nando's UAE",
  description:
    "Open roles in our UAE restaurants and support centre. See what's on and apply from your phone in a few minutes.",
  canonical: careersShareUrl(),
};

// The rooster's dotted tail, as a ring of dots drawn off the hero's edge.
function TailRing() {
  const dots = useMemo(() => {
    const count = 22;
    return Array.from({ length: count }, (_, i) => {
      const angle = ((110 + (i * 300) / (count - 1)) * Math.PI) / 180;
      return { x: 400 + 330 * Math.cos(angle), y: 400 + 330 * Math.sin(angle), i };
    });
  }, []);
  return (
    <svg className="cr-hero-ring" viewBox="0 0 800 800" aria-hidden="true" focusable="false">
      {dots.map((d) => (
        <circle key={d.i} cx={d.x} cy={d.y} r="22" />
      ))}
    </svg>
  );
}

function useListParams() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const department = params.get("dept") ?? ALL;
  const sort = SORTS[params.get("sort")] ? params.get("sort") : "newest";

  // replace: typing and filtering should not fill the Back button's history.
  // Defaults are dropped from the URL so a shared link stays short.
  const update = (changes) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(changes).forEach(([key, value]) => {
          if (!value || value === DEFAULTS[key]) next.delete(key);
          else next.set(key, value);
        });
        return next;
      },
      { replace: true },
    );

  return {
    query,
    department,
    sort,
    setQuery: (q) => update({ q }),
    setDepartment: (dept) => update({ dept }),
    setSort: (sort) => update({ sort }),
    reset: () => update({ q: "", dept: ALL }),
  };
}

const matchesQuery = (job, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [job.title, job.department?.name, job.description]
    .filter(Boolean)
    .some((text) => text.toLowerCase().includes(q));
};

function departmentCounts(jobs) {
  const counts = new Map();
  jobs.forEach((job) => {
    const name = job.department?.name;
    if (name) counts.set(name, (counts.get(name) || 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}

function Hero({ count }) {
  return (
    <section className="cr-hero cr-on-red" aria-labelledby="cr-hero-title">
      <TailRing />
      <div className="cr-wrap cr-hero-body">
        <span className="cr-hero-kicker">
          <i aria-hidden="true" />
          {count ? `Hiring now: ${count} open ${count === 1 ? "role" : "roles"}` : "Careers at Nando's UAE"}
        </span>
        <h1 id="cr-hero-title" className="cr-hero-title">
          Build your career at Nando's
        </h1>
        <p className="cr-hero-lede">
          Grill, serve and lead in our restaurants across the UAE, or join the support centre.
          Apply from your phone in a few minutes.
        </p>
      </div>
    </section>
  );
}

// Search and team filter in one card that overlaps the hero.
function Finder({ jobs, query, onQuery, department, onDepartment }) {
  const departments = departmentCounts(jobs);
  return (
    <div className="cr-wrap cr-finder">
      <form className="cr-finder-card" role="search" onSubmit={(e) => e.preventDefault()}>
        <label className="cr-finder-field">
          <span className="cr-visually-hidden">Search roles</span>
          <LuSearch aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search roles, e.g. cook"
            enterKeyHint="search"
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              className="cr-search-clear"
              onClick={() => onQuery("")}
              aria-label="Clear search"
            >
              <LuX />
            </button>
          )}
        </label>
        <div className="cr-finder-team">
          <Select
            id="cr-team"
            variant="bare"
            icon={<LuUsers />}
            title="Team"
            value={department}
            onChange={onDepartment}
            options={[
              { value: ALL, label: "All teams", hint: String(jobs.length) },
              ...departments.map(([name, n]) => ({ value: name, label: name, hint: String(n) })),
            ]}
            align="right"
          />
        </div>
      </form>
    </div>
  );
}

function ListHead({ total, shown, sort, onSort }) {
  return (
    <div className="cr-list-head">
      <p className="cr-list-count" role="status">
        Open roles{" "}
        <span>{shown === total ? total : `${shown} of ${total}`}</span>
      </p>
      <div className="cr-sort">
        <span id="cr-sort-label">Sort</span>
        <Select
          id="cr-sort"
          variant="compact"
          title="Sort roles"
          labelledBy="cr-sort-label"
          value={sort}
          onChange={onSort}
          options={Object.entries(SORTS).map(([key, { label }]) => ({ value: key, label }))}
          align="right"
        />
      </div>
    </div>
  );
}

function Results({ status, jobs, visible, query, onReset, onRetry }) {
  if (status === "pending") return <RoleListSkeleton />;
  if (status === "error") {
    return (
      <div className="cr-state" role="alert">
        <h2 className="cr-h2">We couldn't load the roles</h2>
        <p className="cr-muted">Check your connection and try again.</p>
        <button type="button" className="cr-btn cr-btn--primary" onClick={onRetry}>
          Try again
        </button>
      </div>
    );
  }
  if (!jobs.length) {
    return (
      <div className="cr-state">
        <h2 className="cr-h2">No open roles right now</h2>
        <p className="cr-muted">
          We post new roles here as soon as they open. Check back soon.
        </p>
      </div>
    );
  }
  if (!visible.length) {
    return (
      <div className="cr-state">
        <h2 className="cr-h2">{query ? `No roles match “${query}”` : "No roles in this team"}</h2>
        <p className="cr-muted">Try another word, or see every open role.</p>
        <button type="button" className="cr-btn cr-btn--secondary" onClick={onReset}>
          Show all roles
        </button>
      </div>
    );
  }
  return <RoleList jobs={visible} label="Open roles" />;
}

const HOW = [
  {
    icon: LuTimer,
    title: "Apply in minutes",
    text: "Your details, your CV and a few quick questions. Answers save as you go.",
  },
  {
    icon: LuMailCheck,
    title: "Instant confirmation",
    text: "You get an email straight away with your reference number.",
  },
  {
    icon: LuMessageCircle,
    title: "We'll be in touch",
    text: "If you're shortlisted, our team contacts you to arrange an interview.",
  },
];

function HowItWorks() {
  return (
    <section className="cr-how" aria-labelledby="cr-how-title">
      <h2 id="cr-how-title" className="cr-h2">
        How applying works
      </h2>
      <ol className="cr-how-steps">
        {HOW.map(({ icon: Icon, title, text }) => (
          <li key={title} className="cr-how-step">
            <span className="cr-how-icon" aria-hidden="true">
              <Icon />
            </span>
            <div>
              <h3 className="cr-h3">{title}</h3>
              <p>{text}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

export default function CareersHome() {
  useDocumentMeta(META);
  const { data: jobs = [], status, refetch } = useOpenJobs();
  const { query, department, sort, setQuery, setDepartment, setSort, reset } = useListParams();

  const visible = useMemo(
    () =>
      jobs
        .filter((job) => department === ALL || job.department?.name === department)
        .filter((job) => matchesQuery(job, query))
        // The sample role (review builds only) always sits last.
        .sort((a, b) => Boolean(a.demo) - Boolean(b.demo) || SORTS[sort].compare(a, b)),
    [jobs, department, query, sort],
  );

  return (
    <CareersShell>
      <TopBar />
      <Hero count={jobs.filter((job) => !job.demo).length} />
      <main id="cr-main" className="cr-main">
        <Finder
          jobs={jobs}
          query={query}
          onQuery={setQuery}
          department={department}
          onDepartment={setDepartment}
        />
        <section className="cr-wrap" aria-label="Open roles">
          {status === "success" && jobs.length > 0 && (
            <ListHead total={jobs.length} shown={visible.length} sort={sort} onSort={setSort} />
          )}
          {status !== "success" && <div style={{ height: 32 }} />}
          <Results
            status={status}
            jobs={jobs}
            visible={visible}
            query={query}
            onReset={reset}
            onRetry={refetch}
          />
          <HowItWorks />
        </section>
      </main>
    </CareersShell>
  );
}
